import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';
import Button from './Button';
import Select from './Select';
import { Checkbox } from './Checkbox';

const ExportModal = ({ isOpen, onClose, data, title = 'Export Prompts' }) => {
  const [format, setFormat] = useState('text');
  const [includeMetadata, setIncludeMetadata] = useState(true);
  const [includeTimestamps, setIncludeTimestamps] = useState(false);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event?.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const formatOptions = [
    { value: 'text', label: 'Plain Text (.txt)', description: 'Simple text format' },
    { value: 'json', label: 'JSON (.json)', description: 'Structured data format' },
    { value: 'markdown', label: 'Markdown (.md)', description: 'Formatted text with markup' },
    { value: 'csv', label: 'CSV (.csv)', description: 'Spreadsheet compatible' }
  ];

  const handleExport = () => {
    const exportData = {
      format,
      includeMetadata,
      includeTimestamps,
      data,
      timestamp: new Date()?.toISOString()
    };

    console.log('Exporting data:', exportData);

    const safeData = Array.isArray(data) ? data : [];

    let content = '';
    let filename = `prompts_${Date.now()}`;
    let mimeType = 'text/plain';

    switch (format) {
      case 'json':
        content = JSON.stringify(exportData, null, 2);
        filename += '.json';
        mimeType = 'application/json';
        break;
      case 'markdown':
        content = `# ${title}\n\n${safeData?.map(item => `## ${item?.title}\n\n${item?.content}\n\n`)?.join('')}`;
        filename += '.md';
        mimeType = 'text/markdown';
        break;
      case 'csv':
        content = `Title,Content,Category,Date\n${safeData?.map(item => `"${item?.title}","${item?.content}","${item?.category}","${item?.date}"`)?.join('\n')}`;
        filename += '.csv';
        mimeType = 'text/csv';
        break;
      default:
        content = safeData?.map(item => `${item?.title}\n\n${item?.content}\n\n---\n\n`)?.join('');
        filename += '.txt';
    }

    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);
    URL.revokeObjectURL(url);

    onClose();
  };

  const handleOverlayClick = (e) => {
    if (e?.target === e?.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="modal-content animate-scaleIn">
        <div className="modal-header">
          <div className="flex items-center justify-between">
            <h2 className="modal-title">{title}</h2>
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Close modal"
            >
              <Icon name="X" size={24} />
            </button>
          </div>
        </div>

        <div className="modal-body space-y-6">
          <div>
            <Select
              label="Export Format"
              description="Choose the file format for your export"
              options={formatOptions}
              value={format}
              onChange={setFormat}
            />
          </div>

          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-foreground">Export Options</h3>
            
            <Checkbox
              label="Include Metadata"
              description="Add category, tags, and other metadata"
              checked={includeMetadata}
              onChange={(e) => setIncludeMetadata(e?.target?.checked)}
            />

            <Checkbox
              label="Include Timestamps"
              description="Add creation and modification dates"
              checked={includeTimestamps}
              onChange={(e) => setIncludeTimestamps(e?.target?.checked)}
            />
          </div>

          <div className="bg-muted rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={20} className="text-primary flex-shrink-0 mt-0.5" />
              <div className="text-sm text-muted-foreground">
                <p className="font-medium text-foreground mb-1">Export Preview</p>
                <p>
                  {Array.isArray(data) ? data?.length : 0} prompt{(Array.isArray(data) ? data?.length : 0) !== 1 ? 's' : ''} will be exported in {formatOptions?.find(f => f?.value === format)?.label} format.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="default" onClick={handleExport} iconName="Download" iconPosition="left">
            Export
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ExportModal;